/**
 * Purpose: Utility functions for formatting display text in the Reservations page.
 * 
 * High-level Workflow:
 * 1. formatFreshness: Calculates and formats time since last data update, with staleness warnings
 * 2. getPropertyShortName: Resolves property display name using priority order
 * 
 * Assumptions & Invariants:
 * - formatFreshness: metadata.snapshot.max_captured_at is a valid ISO date string or null
 * - formatFreshness: metadata.staleness.is_stale and threshold_hours are provided by API
 * - getPropertyShortName: Property object may have multiple name fields, priority order applies
 * - All formatting uses plain language for user-facing text
 * 
 * Dependencies:
 * Internal: None
 * External: None
 * 
 * Version: 1.0.0
 * Created: 2026-01-26
 * Change Log:
 * - 2026-01-26: Initial extraction from ReservationsPage.jsx
 */

/**
 * Format freshness display text
 * 
 * Calculates time since last data update and formats as human-readable text.
 * Shows warning if data is stale (exceeds threshold hours).
 * 
 * @param {Object|null} metadata - Metadata object from API response
 * @returns {string} - Formatted freshness text
 */
export function formatFreshness(metadata) {
  if (!metadata?.snapshot?.max_captured_at) {
    return 'Data updated: unknown (no reservations returned)';
  }
  
  const maxCapturedAt = new Date(metadata.snapshot.max_captured_at);
  const now = new Date();
  const diffMs = now - maxCapturedAt;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  let timeAgo;
  if (diffHours >= 1) {
    timeAgo = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else {
    timeAgo = `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const isStale = metadata.staleness?.is_stale || false;
  const thresholdHours = metadata.staleness?.threshold_hours || 4;
  
  if (isStale) {
    return `Warning: Data is ${diffHours} hours old (threshold: ${thresholdHours} hours)`;
  }
  
  return `Data updated: ${timeAgo}`;
}

/**
 * Get property short name for display
 * 
 * Resolves property display name using priority order:
 * display_name > clc_short_name > name > property_name > 'Unnamed Property'
 * 
 * This matches the Calendar page logic for consistent property naming across pages.
 * 
 * @param {Object|null} property - Property object from SessionContext
 * @returns {string} - Property display name
 */
export function getPropertyShortName(property) {
  return property?.display_name || property?.clc_short_name || property?.name || property?.property_name || 'Unnamed Property';
}
